import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  MessageSquare, AlertTriangle, Clock, CheckCircle, Building, MapPin, Shield,
  Filter, Search, X, Download, User, ChevronDown, Users as UsersIcon,
} from 'lucide-react';
import { mdToSafeHtml } from '../../lib/markdown';

const EMPTY_FILTERS = { user_id: '', carrier: '', state: '', lob: '', confidence: '', query_type: '', has_answer: '', search: '', date_from: '', date_to: '' };

const exportQueriesCSV = (queries) => {
  const rows = [['Date','User','Question','Confidence','Carrier','State','LOB','Answered','Citations','Answer']].concat(
    queries.map(q => [q.created_at?.slice(0,19).replace('T',' '), q.user_name, q.question, q.confidence, q.filters?.carrier || '', q.filters?.state || '', q.filters?.lob || '', q.has_answer ? 'Yes' : 'No', q.citation_count, (q.answer || '').replace(/\n/g,' ').slice(0, 500)])
  );
  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `appetite-queries-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
};

const StatsCards = ({ queryStats }) => (
  <>
    <div className="grid grid-cols-4 gap-3">
      {[
        { label: 'Total Questions (30d)', value: queryStats.total_queries, icon: MessageSquare, color: '#3b82f6' },
        { label: 'No-Answer Rate', value: `${queryStats.no_answer_rate}%`, icon: AlertTriangle, color: '#ef4444', sub: `${queryStats.no_answer_count} unanswered` },
        { label: 'Low Confidence', value: `${queryStats.low_confidence_rate}%`, icon: Clock, color: '#f59e0b', sub: `${queryStats.low_confidence_count} low-conf` },
        { label: 'High Confidence', value: `${queryStats.high_confidence_rate}%`, icon: CheckCircle, color: '#16a34a', sub: `${queryStats.high_confidence_count} high-conf` },
      ].map(m => (
        <Card key={m.label} className="p-4 border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <m.icon size={14} style={{ color: m.color }} />
            <span className="text-[10px] text-gray-400 font-medium">{m.label}</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{m.value}</p>
          {m.sub && <p className="text-[10px] text-gray-400 mt-0.5">{m.sub}</p>}
        </Card>
      ))}
    </div>

    <div className="grid grid-cols-3 gap-3">
      <Card className="p-3 border-gray-200">
        <h3 className="text-[10px] font-bold text-gray-400 uppercase mb-2 flex items-center gap-1"><UsersIcon size={10} /> Top Askers</h3>
        {queryStats.top_users.length === 0 && <p className="text-[11px] text-gray-400">No data yet</p>}
        {queryStats.top_users.slice(0, 5).map((u, i) => (
          <div key={u.user_id || `${u.user_name || 'unknown'}-${i}`} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
            <span className="text-xs text-gray-700">{u.user_name || 'Unknown'}</span>
            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{u.count}</span>
          </div>
        ))}
      </Card>
      <Card className="p-3 border-gray-200">
        <h3 className="text-[10px] font-bold text-gray-400 uppercase mb-2 flex items-center gap-1"><Building size={10} /> Top Carriers Asked</h3>
        {queryStats.top_carriers.length === 0 && <p className="text-[11px] text-gray-400">No data yet</p>}
        {queryStats.top_carriers.slice(0, 5).map(c => (
          <div key={c.carrier} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
            <span className="text-xs text-gray-700">{c.carrier}</span>
            <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded">{c.count}</span>
          </div>
        ))}
      </Card>
      <Card className="p-3 border-gray-200">
        <h3 className="text-[10px] font-bold text-gray-400 uppercase mb-2 flex items-center gap-1"><MessageSquare size={10} /> Most-Asked Questions</h3>
        {queryStats.top_questions.length === 0 && <p className="text-[11px] text-gray-400">No data yet</p>}
        {queryStats.top_questions.slice(0, 4).map((q, i) => (
          <div key={`${q.question}-${i}`} className="py-1 border-b border-gray-50 last:border-0">
            <p className="text-[11px] text-gray-700 truncate" title={q.question}>{q.question}</p>
            <span className="text-[9px] text-gray-400">asked {q.count}x</span>
          </div>
        ))}
      </Card>
    </div>
  </>
);

const QueryRow = ({ q, isOpen, onToggle }) => {
  const confColor = q.confidence === 'high' ? 'bg-green-100 text-green-700' : q.confidence === 'low' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700';
  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-testid={`query-${q.id}`}>
      <button onClick={onToggle} className="w-full text-left p-3 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <MessageSquare size={14} className="text-blue-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase ${confColor}`}>{q.confidence}</span>
            {q.query_type === 'compare' && <span className="text-[9px] font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded uppercase">Compare</span>}
            {!q.has_answer && <span className="text-[9px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded uppercase">No Data</span>}
            <span className="text-xs text-gray-900 font-medium flex-1 truncate">{q.question}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] text-gray-500">
            <span className="flex items-center gap-0.5"><User size={9} /> {q.user_name || q.user_email}</span>
            {q.filters?.carrier && <span className="flex items-center gap-0.5 text-blue-600"><Building size={9} /> {q.filters.carrier}</span>}
            {q.filters?.state && <span className="flex items-center gap-0.5 text-green-600"><MapPin size={9} /> {q.filters.state}</span>}
            {q.filters?.lob && <span className="flex items-center gap-0.5 text-purple-600"><Shield size={9} /> {q.filters.lob}</span>}
            <span className="text-gray-400 ml-auto">{new Date(q.created_at).toLocaleString()}</span>
          </div>
        </div>
        <ChevronDown size={14} className={`text-gray-300 transition-transform mt-1 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-3 pb-3 border-t border-gray-100 pt-2 ml-11">
          <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Answer Given</p>
          <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed bg-gray-50 rounded p-2 border border-gray-100"
            dangerouslySetInnerHTML={{ __html: mdToSafeHtml(q.answer || '—') }} />
          {q.citations?.length > 0 && (
            <>
              <p className="text-[9px] font-bold text-gray-400 uppercase mt-2 mb-1">Sources ({q.citation_count})</p>
              <div className="space-y-1">
                {q.citations.map((c, ci) => (
                  <div key={c.doc_id || ci} className="text-[10px] text-gray-600 bg-gray-50 rounded px-2 py-1 border border-gray-100">
                    <span className="font-bold">{c.carrier}</span>
                    {c.state && <span> / {c.state}</span>}
                    {c.lob && <span> / {c.lob}</span>}
                    {c.title && <span className="text-gray-500"> — {c.title}</span>}
                    {c.effective_date && <span className="text-gray-400"> (Eff: {c.effective_date})</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const QueriesTab = ({ queries, queriesTotal, queryStats, qFilters, setQFilters, loadQueries, users, filters, expandedQuery, setExpandedQuery }) => (
  <div className="flex-1 overflow-y-auto p-6 space-y-4">
    {queryStats && <StatsCards queryStats={queryStats} />}

    {/* Filters */}
    <Card className="p-3 border-gray-200">
      <div className="flex items-center gap-2 mb-2">
        <Filter size={12} className="text-gray-400" />
        <span className="text-[11px] font-bold text-gray-600 uppercase">Filter Team Queries</span>
        <span className="text-[10px] text-gray-400 ml-auto">{queriesTotal} result{queriesTotal !== 1 ? 's' : ''}</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <select value={qFilters.user_id} onChange={e => setQFilters(p => ({ ...p, user_id: e.target.value }))} className="h-8 text-xs border rounded px-2 bg-white" data-testid="q-filter-user">
          <option value="">All Team Members</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
        <select value={qFilters.carrier} onChange={e => setQFilters(p => ({ ...p, carrier: e.target.value }))} className="h-8 text-xs border rounded px-2 bg-white" data-testid="q-filter-carrier">
          <option value="">All Carriers</option>
          {filters.carriers.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={qFilters.state} onChange={e => setQFilters(p => ({ ...p, state: e.target.value }))} className="h-8 text-xs border rounded px-2 bg-white" data-testid="q-filter-state">
          <option value="">All States</option>
          {filters.states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={qFilters.lob} onChange={e => setQFilters(p => ({ ...p, lob: e.target.value }))} className="h-8 text-xs border rounded px-2 bg-white" data-testid="q-filter-lob">
          <option value="">All Lines</option>
          {filters.lobs.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={qFilters.confidence} onChange={e => setQFilters(p => ({ ...p, confidence: e.target.value }))} className="h-8 text-xs border rounded px-2 bg-white" data-testid="q-filter-confidence">
          <option value="">Any Confidence</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select value={qFilters.has_answer} onChange={e => setQFilters(p => ({ ...p, has_answer: e.target.value }))} className="h-8 text-xs border rounded px-2 bg-white" data-testid="q-filter-hasanswer">
          <option value="">Answered or Not</option>
          <option value="true">Got Answer</option>
          <option value="false">No Answer</option>
        </select>
        <select value={qFilters.query_type} onChange={e => setQFilters(p => ({ ...p, query_type: e.target.value }))} className="h-8 text-xs border rounded px-2 bg-white" data-testid="q-filter-type">
          <option value="">Chat + Compare</option>
          <option value="chat">Chat</option>
          <option value="compare">Compare</option>
        </select>
        <Input type="search" value={qFilters.search} onChange={e => setQFilters(p => ({ ...p, search: e.target.value }))} placeholder="Search in Q or A…" className="h-8 text-xs" data-testid="q-filter-search" />
        <div className="flex items-center gap-1">
          <Input type="date" value={qFilters.date_from} onChange={e => setQFilters(p => ({ ...p, date_from: e.target.value }))} className="h-8 text-xs flex-1" data-testid="q-filter-from" />
          <span className="text-[10px] text-gray-400">to</span>
          <Input type="date" value={qFilters.date_to} onChange={e => setQFilters(p => ({ ...p, date_to: e.target.value }))} className="h-8 text-xs flex-1" data-testid="q-filter-to" />
        </div>
        <Button size="sm" onClick={loadQueries} className="h-8 text-xs bg-[#101C47] gap-1" data-testid="q-apply">
          <Search size={11} /> Apply
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setQFilters({ ...EMPTY_FILTERS }); setTimeout(loadQueries, 50); }} className="h-8 text-xs gap-1" data-testid="q-clear">
          <X size={11} /> Clear
        </Button>
        <Button size="sm" variant="outline" onClick={() => exportQueriesCSV(queries)} className="h-8 text-xs gap-1" data-testid="q-export-csv">
          <Download size={11} /> CSV
        </Button>
      </div>
    </Card>

    {/* Query list */}
    <div className="space-y-1.5" data-testid="team-queries-list">
      {queries.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
          <MessageSquare size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-400 text-sm">No queries match the current filters</p>
        </div>
      )}
      {queries.map(q => (
        <QueryRow key={q.id} q={q} isOpen={expandedQuery === q.id} onToggle={() => setExpandedQuery(expandedQuery === q.id ? null : q.id)} />
      ))}
    </div>
  </div>
);
